import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { SortableItem } from "./SortableItem";

interface FileItem {
  id: string | number;
  path: string;
  file?: {
    nameOriginal?: string;
    name?: string;
  };
}

interface SortableImagesListProps {
  images: FileItem[];
  onImagesChange: (updated: FileItem[]) => void;
  onDeleteImage: (img: FileItem) => void;
  onSaveOrder?: (newOrder: FileItem[]) => Promise<void>;
  setCurrentProduct: any;
}

export const SortableImagesList: React.FC<SortableImagesListProps> = ({
  images,
  onImagesChange,
  onDeleteImage,
  onSaveOrder,
  setCurrentProduct,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDownload = async (img: FileItem) => {
    try {
      const response = await fetch(img.path, {
        mode: "cors",
      });
      if (!response.ok) throw new Error("Network response was not ok");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        img.file?.nameOriginal || img.file?.name || img.path.split("/").pop();
      link.download = filename || "file";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);
      const newImages = arrayMove(images, oldIndex, newIndex);
      onImagesChange(newImages);

      if (onSaveOrder) {
        await onSaveOrder(newImages);
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={images} strategy={verticalListSortingStrategy}>
        <ul className="list-group mb-3">
          {images.map((img) => (
            <SortableItem
              key={img.id}
              img={img}
              onDelete={onDeleteImage}
              onDownload={handleDownload}
              setCurrentProduct={setCurrentProduct}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};
